import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Select,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Image,
  Descriptions,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EyeOutlined,
  StopOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { getTemplateList, getTemplate, reviewTemplate, offlineTemplate, deleteTemplate, setTemplateKeywords } from '../services/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const TemplateList = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ status: undefined });
  const [previewHost, setPreviewHost] = useState('');
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [keywordsModalVisible, setKeywordsModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [rejectForm] = Form.useForm();
  const [keywordsForm] = Form.useForm();

  useEffect(() => {
    loadTemplates();
  }, [pagination.current, pagination.pageSize]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await getTemplateList({
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filters,
      });
      setTemplates(data.list || []);
      setPagination((prev) => ({ ...prev, total: data.total }));
      setPreviewHost(data.preview_host || '');
    } catch (error) {
      console.error('加载模板失败', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    loadTemplates();
  };

  const handleViewDetail = async (record) => {
    try {
      const data = await getTemplate(record.id);
      setSelectedTemplate(data);
      setDetailModalVisible(true);
    } catch (error) {
      console.error('获取模板详情失败', error);
    }
  };

  const handleApprove = async (id) => {
    try {
      await reviewTemplate(id, { action: 'approve' });
      message.success('模板已通过审核');
      loadTemplates();
    } catch (error) {
      console.error('审核失败', error);
    }
  };

  const handleRejectClick = (record) => {
    setSelectedTemplate(record);
    rejectForm.resetFields();
    setRejectModalVisible(true);
  };

  const handleRejectSubmit = async () => {
    try {
      const values = await rejectForm.validateFields();
      await reviewTemplate(selectedTemplate.id, {
        action: 'reject',
        reject_reason: values.reject_reason,
      });
      message.success('模板已拒绝');
      setRejectModalVisible(false);
      loadTemplates();
    } catch (error) {
      console.error('拒绝失败', error);
    }
  };

  const handleOffline = async (id) => {
    try {
      await offlineTemplate(id);
      message.success('模板已下架');
      loadTemplates();
    } catch (error) {
      console.error('下架失败', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTemplate(id);
      message.success('模板已删除');
      loadTemplates();
    } catch (error) {
      console.error('删除失败', error);
    }
  };

  const handleKeywordsClick = (record) => {
    setSelectedTemplate(record);
    keywordsForm.setFieldsValue({ keywords: record.keywords || '' });
    setKeywordsModalVisible(true);
  };

  const handleKeywordsSubmit = async () => {
    try {
      const values = await keywordsForm.validateFields();
      await setTemplateKeywords(selectedTemplate.id, { keywords: values.keywords });
      message.success('关键字设置成功');
      setKeywordsModalVisible(false);
      loadTemplates();
    } catch (error) {
      console.error('设置关键字失败', error);
    }
  };

  const getStatusTag = (status, statusStr) => {
    const colorMap = {
      0: 'orange',
      1: 'green',
      2: 'red',
      3: 'default',
    };
    return <Tag color={colorMap[status]}>{statusStr}</Tag>;
  };

  // 获取完整的贴纸URL
  const getPreviewUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return previewHost ? `${previewHost}/${url}` : url;
  };

  const columns = [
    {
      title: '预览',
      dataIndex: 'preview_url',
      key: 'preview_url',
      fixed: 'left',
      width: 200,
      render: (url) =>
        url ? (
          <Image
            src={getPreviewUrl(url)}
            style={{ objectFit: 'contain' }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgesAJPFdXLgAAA=="
          />
        ) : (
          <div
            style={{
              width: 150,
              height: 30,
              background: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              fontSize: 12,
            }}
          >
            无预览
          </div>
        ),
    },
    {
      title: '模板名称',
      dataIndex: 'name',
      key: 'name',
      width: 100,
      ellipsis: true,
    },
    {
      title: '尺寸',
      key: 'size',
      width: 100,
      render: (_, record) => `${record.width} x ${record.height}`,
    },    
    {
      title: '上传用户',
      key: 'user',
      width: 120,
      render: (_, record) => (
        <div>
          <div>{record.user?.username || '-'}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.user?.nickname || '-'}</div>
        </div>
      ),
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, record) => getStatusTag(record.status, record.status_str),
    },
    {
      title: '关键字',
      key: 'keywords',
      width: 150,
      render: (_, record) => record.keywords || '-',
    },
    {
      title: '统计',
      key: 'stats',
      width: 120,
      render: (_, record) => (
        <div>
          <div>浏览: {record.view_count}</div>
          <div>收藏: {record.favorite_count}</div>
        </div>
      ),
    },
    {
      title: '上传时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <Space wrap>
          <Tooltip title="查看详情">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            >
              详情
            </Button>
          </Tooltip>
          <Tooltip title="设置关键字">
            <Button
              type="link"
              size="small"
              icon={<TagOutlined />}
              onClick={() => handleKeywordsClick(record)}
            >
              关键字
            </Button>
          </Tooltip>
          {record.status === 0 && (
            <>
              <Popconfirm
                title="确定要通过该模板吗？"
                onConfirm={() => handleApprove(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" size="small" icon={<CheckOutlined />} style={{ color: '#52c41a' }}>
                  通过
                </Button>
              </Popconfirm>
              <Button
                type="link"
                size="small"
                danger
                icon={<CloseOutlined />}
                onClick={() => handleRejectClick(record)}
              >
                拒绝
              </Button>
            </>
          )}
          {record.status === 1 && (
            <Popconfirm
              title="确定要下架该模板吗？"
              onConfirm={() => handleOffline(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" size="small" icon={<StopOutlined />} style={{ color: '#faad14' }}>
                下架
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="确定要删除该模板吗？此操作不可恢复"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>模板管理</h2>
        <p>管理用户上传的模板，审核、下架或删除模板</p>
      </div>

      <Card bordered={false}>
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder="模板状态"
            style={{ width: 140 }}
            allowClear
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
          >
            <Option value={0}>待审核</Option>
            <Option value={1}>已通过</Option>
            <Option value={2}>已拒绝</Option>
            <Option value={3}>已下架</Option>
          </Select>
          <Button type="primary" onClick={handleSearch}>
            筛选
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize }),
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="模板详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedTemplate && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="模板ID">{selectedTemplate.id}</Descriptions.Item>
            <Descriptions.Item label="UUID">{selectedTemplate.uuid}</Descriptions.Item>
            <Descriptions.Item label="模板名称">{selectedTemplate.name}</Descriptions.Item>
            <Descriptions.Item label="尺寸">
              {selectedTemplate.width} x {selectedTemplate.height}
            </Descriptions.Item>
            <Descriptions.Item label="上传用户">
              {selectedTemplate.user?.username} ({selectedTemplate.user?.nickname})
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {getStatusTag(selectedTemplate.status, selectedTemplate.status_str)}
            </Descriptions.Item>
            <Descriptions.Item label="浏览次数">{selectedTemplate.view_count}</Descriptions.Item>
            <Descriptions.Item label="收藏次数">{selectedTemplate.favorite_count}</Descriptions.Item>
            <Descriptions.Item label="上传时间">
              {dayjs(selectedTemplate.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="审核时间">
              {selectedTemplate.reviewed_at
                ? dayjs(selectedTemplate.reviewed_at).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            {selectedTemplate.reject_reason && (
              <Descriptions.Item label="拒绝原因" span={2}>
                <span style={{ color: '#ff4d4f' }}>{selectedTemplate.reject_reason}</span>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="预览图" span={2}>
              {selectedTemplate.preview_url ? (
                <Image
                  src={`${process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || ''}/${selectedTemplate.preview_url}`}
                  style={{ maxWidth: '100%', maxHeight: 300 }}
                />
              ) : (
                '无'
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* 拒绝弹窗 */}
      <Modal
        title="拒绝模板"
        open={rejectModalVisible}
        onOk={handleRejectSubmit}
        onCancel={() => setRejectModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="reject_reason"
            label="拒绝原因"
            rules={[
              { required: true, message: '请输入拒绝原因' },
              { max: 500, message: '拒绝原因不能超过500个字符' },
            ]}
          >
            <TextArea rows={4} placeholder="请输入拒绝原因，例如：内容不符合规范" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 关键字设置弹窗 */}
      <Modal
        title="设置关键字"
        open={keywordsModalVisible}
        onOk={handleKeywordsSubmit}
        onCancel={() => setKeywordsModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <Form form={keywordsForm} layout="vertical">
          <Form.Item
            name="keywords"
            label="关键字"
            rules={[
              { max: 500, message: '关键字不能超过500个字符' },
            ]}
          >
            <TextArea rows={4} placeholder="请输入关键字，多个关键字用逗号分隔，例如：节日,圣诞,新年" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TemplateList;
